/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Link as ReactRouterLink } from 'react-router';

import { Link, Typography } from '@coursera/cds-core';
import { Button } from '@coursera/coursera-ui';
import { SvgLoaderSignal } from '@coursera/coursera-ui/svg';

import ProductListWithExpandToggle from 'bundles/course-cards/components/course-card/enterprise/ProductListWithExpandToggle';
import CourseCompletionBanner from 'bundles/enterprise-legacy-learner-home/components/SkillSetCourseCompletionBanner';
import { toProgramSkillSet } from 'bundles/enterprise-legacy-learner-home/links';
import type { ProductStub } from 'bundles/enterprise-legacy-learner-home/types/Products';
import Dropdown from 'bundles/phoenix/components/Dropdown';
import type { PropsFromSaveSkillSetToggle } from 'bundles/program-home/components/ProgramHomeApp';
import { org_coursera_enterprise_skills_TargetSkillProfileUserGoalState as SkillProfileStates } from 'bundles/program-home/components/__generated__/globalTypes';

import _t from 'i18n!nls/program-skills-objectives';

type PropsFromCaller = PropsFromSaveSkillSetToggle & {
  targetSkillProfileId: string;
  tspTitle?: string;
  objectiveSlug?: string;
  products: Array<ProductStub>;
  programId: string;
  programSlug: string;
  userId: number;
  thirdPartyOrganizationId: string;
};

export type Props = PropsFromCaller;

const SkillSetEnrolledProducts = ({
  tspTitle,
  targetSkillProfileId,
  objectiveSlug,
  products,
  programId,
  programSlug,
  userId,
  onSkillSetSaveToggle,
  thirdPartyOrganizationId,
}: Props) => {
  const [saveInProgress, setSaveInProgress] = React.useState(false);

  // ---

  const styles = {
    titleContainer: css`
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: var(--cds-spacing-150);
    `,
    actionsContainer: css`
      display: flex;
    `,
    tspLink: css`
      margin-right: var(--cds-spacing-200);
    `,
  };

  const hasCompletedProducts = products.some((product) => product.isCompleted);
  const hasInProgressProducts = products.some((product) => product.isEnrolled && !product.isCompleted);
  const showCourseCompletionBanner = (hasCompletedProducts && !hasInProgressProducts) || !products.length;
  const dropdownLabel = _t('SkillSet actions for #{tspTitle}', { tspTitle });
  const dropdownItem = (
    <Button
      label={_t('Remove from saved')}
      onClick={() =>
        onSkillSetSaveToggle({ targetSkillProfileId, state: SkillProfileStates.UNSAVED, setSaveInProgress })
      }
      type="noStyle"
      size="zero"
    />
  );

  return (
    <div className="rc-SkillSetEnrolledProducts">
      <div css={styles.titleContainer}>
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography component="p" variant="h2">
          {tspTitle}
        </Typography>
        <div css={styles.actionsContainer}>
          {objectiveSlug && programSlug && (
            <Link
              component={ReactRouterLink}
              variant="quiet"
              to={toProgramSkillSet(programSlug, objectiveSlug)}
              aria-label={_t('View SkillSet: #{name}', { name: tspTitle })}
              css={styles.tspLink}
              typographyVariant="bodyPrimary"
            >
              {_t('View SkillSet')}
            </Link>
          )}
          {saveInProgress ? (
            <SvgLoaderSignal size={10} />
          ) : (
            <Dropdown
              dropdownId={`${tspTitle}-dropdown-menu`}
              label={dropdownLabel}
              listItems={[dropdownItem]}
              iconClassName="ellipsis-h"
            />
          )}
        </div>
      </div>
      {showCourseCompletionBanner && objectiveSlug && programSlug && (
        <CourseCompletionBanner href={toProgramSkillSet(programSlug, objectiveSlug)} tspTitle={tspTitle} />
      )}
      {products.length > 0 && (
        <ProductListWithExpandToggle
          products={products}
          programId={programId}
          programSlug={programSlug}
          userId={userId}
          onSkillSetSaveToggle={onSkillSetSaveToggle}
          viewMoreLessContext={_t('courses from SkillSet: #{skillSetName}', { skillSetName: tspTitle })}
          thirdPartyOrganizationId={thirdPartyOrganizationId}
        />
      )}
    </div>
  );
};

export default SkillSetEnrolledProducts;
